import React, { FC, useContext, useEffect, useMemo } from "react";
import { useParams } from "react-router";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Grid, Skeleton, Alert, Link, useTheme } from "@mui/material";
import ImageGallery from "react-image-gallery";
import "react-image-gallery/styles/css/image-gallery.css";
import DOMPurify from "dompurify";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import TelegramIcon from "@mui/icons-material/Telegram";
import VerifiedIcon from "@mui/icons-material/Verified";
import { VisitsLabel } from "../../components/VisitsLabel";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

import { ProfileTable, ServicesTable } from "@components";
import { BackdropContext, HomeModelsContext } from "@contexts";
import { api, sendAnalyticsEvent } from "@utils";
import { useQuery } from "@tanstack/react-query";

type ImageItem = { imageUrl: string; thumbnailImageUrl: string };
type Attribute = { label: string; value: string };
type Contact = Record<string, string | null>;
type Service = { code: string; included?: boolean; extraPrice?: number | null };

export type ModelResponse = {
  id: number;
  fullname: string;
  shortDescription?: string | null;
  richDescription?: string | null;
  showOnline?: boolean;
  videoUrl?: string | null;
  verified?: boolean;
  rates?: {
    incall?: Record<string, string>;
    outcall?: Record<string, string>;
  } | null;
  images?: ImageItem[];
  attributes?: Attribute[];
  contact?: Contact;
  servicesOffered?: Service[];
};

type GetModelByIdResponse = { model: ModelResponse };

const getModelByIdReq = (id: string) => ({
  url: `/models/${id}`,
  method: "GET" as const,
});

const useModelById = (id?: string) =>
  useQuery({
    queryKey: ["model", id],
    enabled: Boolean(id),
    queryFn: async () => {
      const { data } = await api.request<GetModelByIdResponse>(
        getModelByIdReq(id as string)
      );
      return data;
    },
    select: (d) => d.model,
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  });

const ACCENT = "#d4163c";
const cardSx = {
  bgcolor: "#141418",
  border: "1px solid #23232a",
  borderRadius: "14px",
  boxShadow: "none",
  mb: 2.5,
  overflow: "hidden",
};

const toEmbed = (u: string) => {
  const yt = u.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]{11})/);
  if (yt) return { iframe: true, src: `https://www.youtube.com/embed/${yt[1]}` };
  const vm = u.match(/vimeo\.com\/(\d+)/);
  if (vm) return { iframe: true, src: `https://player.vimeo.com/video/${vm[1]}` };
  return { iframe: false, src: u };
};

const NavBtn: FC<{ label: string; onClick: () => void }> = ({ label, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    style={{
      flex: "none",
      background: ACCENT,
      color: "#fff",
      border: 0,
      borderRadius: 999,
      padding: "6px 18px",
      fontSize: 14,
      fontWeight: 700,
      fontFamily: "inherit",
      cursor: "pointer",
      boxShadow: "0 2px 8px rgba(0,0,0,.4)",
    }}
  >
    {label}
  </button>
);

const Details: FC = () => {
  const { id } = useParams<{ id: string }>();
  const theme = useTheme();
  const { setBackdropIsOpen } = useContext(BackdropContext);
  const { data: model, isFetching, isError, refetch } = useModelById(id);

  // Prev / Next: recorren el mismo listado de la portada (si se entra directo, se arma desde la API)
  const navigate = useNavigate();
  const { homeModels } = useContext(HomeModelsContext);
  const { data: navFallback } = useQuery({
    queryKey: ["nav-models"],
    enabled: !homeModels || homeModels.length === 0,
    queryFn: async () => {
      const { data } = await api.request<{
        models: { topic: string; membership: string | null; verified: boolean }[];
      }>({ url: "/models", method: "GET" });
      const rank = (m: { membership: string | null; verified: boolean }) =>
        m.membership === "VIP" ? 0 : m.membership === "TOP" ? 1 : m.verified ? 2 : 3;
      return (data.models ?? [])
        .map((m, i) => ({ t: m.topic, r: rank(m), i }))
        .sort((a, b) => a.r - b.r || a.i - b.i)
        .map((x) => x.t);
    },
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  });
  const navList: string[] = useMemo(
    () =>
      homeModels && homeModels.length > 0
        ? homeModels.map((m: { topic: string }) => m.topic)
        : navFallback ?? [],
    [homeModels, navFallback]
  );
  const navPos = id ? navList.indexOf(id) : -1;
  const showNav = navPos >= 0 && navList.length > 1;
  const goRel = (d: number) => {
    if (!showNav) return;
    navigate(`/show/${navList[(navPos + d + navList.length) % navList.length]}`);
  };


  useEffect(() => {
    setBackdropIsOpen(isFetching);
  }, [isFetching, setBackdropIsOpen]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [id]);

  const galleryItems = useMemo(
    () =>
      (model?.images ?? []).map((el) => ({
        original: el.imageUrl,
        thumbnail: el.thumbnailImageUrl,
      })),
    [model?.images]
  );

  const sanitizedDescription = useMemo(() => {
    const html = model?.richDescription ?? "";
    return { __html: DOMPurify.sanitize(html) };
  }, [model?.richDescription]);

  if (isError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert
          severity="error"
          action={
            <Typography onClick={() => refetch()} sx={{ cursor: "pointer", fontWeight: 600 }}>
              Reintentar
            </Typography>
          }
        >
          Ocurrió un error al cargar el perfil.
        </Alert>
      </Box>
    );
  }

  if (!model) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="text" height={32} width="40%" />
        <Skeleton variant="text" height={20} width="60%" />
        <Skeleton variant="rectangular" height={400} sx={{ mt: 2, borderRadius: 2 }} />
      </Box>
    );
  }

  const phoneDigits = (model.contact?.phone ?? "").replace(/\D/g, "").replace(/^00/, "");
  const waUrl = `https://wa.me/${phoneDigits}?text=${encodeURIComponent(
    `Hola ${model.fullname} vi tu anuncio en BestEuroGirls.com`
  )}`;
  const tgUrl = `https://t.me/+${phoneDigits}`;
  const track = (label: string) =>
    sendAnalyticsEvent("click", {
      event_category: "Contact models",
      event_label: label,
    });
  const linkSx = {
    display: "flex",
    alignItems: "center",
    gap: 1.25,
    py: 1.1,
    color: "#fff",
    fontSize: 15,
    fontWeight: 600,
    textDecoration: "none",
    borderBottom: "1px solid #23232a",
    "&:hover": { color: "#ff6b86" },
  };

  const fmtRate = (v?: string) => {
    const t = (v ?? "").trim();
    if (!t || t.toLowerCase() === "ask") return null;
    return /^\d+([.,]\d+)?$/.test(t) ? `${t} €` : t;
  };
  const incallRows: [string, string][] = [
    ["m15", "15 minutes"],
    ["m30", "30 minutes"],
    ["m45", "45 minutes"],
    ["h1", "1 hour"],
    ["h2", "2 hours"],
    ["overnight", "Overnight"],
  ];
  const outcallRows: [string, string][] = [
    ["h1", "1 hour"],
    ["h2", "2 hours"],
    ["overnight", "Overnight"],
  ];
  const renderRates = (group: "incall" | "outcall", rows: [string, string][]) =>
    rows.map(([k, label]) => {
      const v = fmtRate(model.rates?.[group]?.[k]);
      return (
        <Box
          key={`${group}-${k}`}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            py: "6px",
            borderBottom: "1px solid #23232a",
            fontSize: 13,
          }}
        >
          <Box sx={{ color: "#8a8a92", fontWeight: 600 }}>{label}</Box>
          <Box sx={{ color: v ? "#fff" : "#777", fontWeight: v ? 700 : 400 }}>
            {v ?? "ask"}
          </Box>
        </Box>
      );
    });

  const video = model.videoUrl ? toEmbed(model.videoUrl) : null;

  return (
    <Box sx={{ p: 0, mt: { xs: -2, md: -3 } }}>
      <Box sx={{ mb: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
          {showNav ? <NavBtn label="‹ Prev" onClick={() => goRel(-1)} /> : <Box />}
          <Typography
            sx={{
              fontSize: { xs: 24, md: 28 },
              fontWeight: 800,
              color: "#fff",
              textAlign: "center",
              flex: 1,
              minWidth: 0,
            }}
          >
            {model.fullname}
          </Typography>
          {showNav ? <NavBtn label="Next ›" onClick={() => goRel(1)} /> : <Box />}
        </Box>
        {model.shortDescription ? (
          <Typography
            sx={{
              fontStyle: "italic",
              color: "#aaa",
              textAlign: "center",
              mt: "2px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {model.shortDescription}
          </Typography>
        ) : null}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexWrap: "wrap",
            gap: 1.25,
            mt: 0.75,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              px: 1.5,
              py: 0.5,
              borderRadius: "20px",
              fontSize: 13,
              fontWeight: 700,
              border: "1px solid " + (model.verified ? "#2ecc71" : "#555"),
              color: model.verified ? "#2ecc71" : "#9a9aa2",
            }}
          >
            {model.verified ? (
              <VerifiedIcon sx={{ fontSize: 18 }} />
            ) : (
              <ErrorOutlineIcon sx={{ fontSize: 18 }} />
            )}
            {model.verified ? "Verified photos" : "Not verified"}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{ width: 9, height: 9, borderRadius: "50%", bgcolor: "#2ecc71" }} />
            <Typography sx={{ fontWeight: 700, color: "#2ecc71", fontSize: 14 }}>
              Online - Available
            </Typography>
          </Box>
          {id ? (
            <Box sx={{ "& > *": { marginLeft: "0 !important" } }}>
              <VisitsLabel topic={id} />
            </Box>
          ) : null}
        </Box>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} md={9}>
          <Box
            sx={{
              ...cardSx,
              p: 1,
              "& .image-gallery-image": {
                maxHeight: "72vh",
                objectFit: "contain",
                background: "#0b0b0e",
              },
              "& .image-gallery-slides": { touchAction: "pan-y" },
              "& .image-gallery-thumbnail": { border: "2px solid transparent" },
              "& .image-gallery-thumbnail.active, & .image-gallery-thumbnail:hover": {
                border: "2px solid " + ACCENT,
              },
              "& .image-gallery-content, & .image-gallery-slide-wrapper": {
                background: "transparent",
              },
            }}
          >
            {galleryItems.length > 0 ? (
              <ImageGallery showPlayButton={false} items={galleryItems} />
            ) : (
              <Box
                sx={{
                  height: 320,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#666",
                }}
              >
                Sin imágenes
              </Box>
            )}
          </Box>

          {video ? (
            <Box sx={{ ...cardSx, p: 1 }}>
              <Box sx={{ position: "relative", pt: "56.25%", borderRadius: "10px", overflow: "hidden", bgcolor: "#000" }}>
                {video.iframe ? (
                  <iframe
                    src={video.src}
                    title="video"
                    allowFullScreen
                    style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }}
                  />
                ) : (
                  <video
                    src={video.src}
                    controls
                    style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
                  />
                )}
              </Box>
            </Box>
          ) : null}

          {model.richDescription ? (
            <Box
              sx={{ ...cardSx, p: 2.5, color: "#ddd", lineHeight: 1.7, fontSize: 15, "& *": { color: "#ddd !important", backgroundColor: "transparent !important" }, "& a": { color: "#ff6b86 !important" }, "& p": { margin: "0 0 .8em" } }}
              dangerouslySetInnerHTML={sanitizedDescription}
            />
          ) : null}

          {model.servicesOffered?.some((s) => s.included) ? (
            <Box sx={cardSx}>
              <ServicesTable data={model.servicesOffered} />
            </Box>
          ) : null}
        </Grid>

        <Grid item xs={12} md={3}>
          <Box sx={{ position: { md: "sticky" }, top: 16 }}>
            {phoneDigits ? (
              <Box sx={{ ...cardSx, p: 2 }}>
                <Typography
                  sx={{ fontSize: 12, letterSpacing: ".1em", color: ACCENT, fontWeight: 700, mb: 0.5 }}
                >
                  CONTACT
                </Typography>
                <Link
                  href={waUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={linkSx}
                  onClick={() => track("WhatsApp")}
                >
                  <WhatsAppIcon sx={{ color: "#25D366" }} />
                  {model.contact?.phone}
                </Link>
                <Link
                  href={tgUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ ...linkSx, borderBottom: "none" }}
                  onClick={() => track("Telegram")}
                >
                  <TelegramIcon sx={{ color: "#29A9EB" }} />
                  Telegram
                </Link>
              </Box>
            ) : null}
            {model.attributes?.length ? (
              <Box sx={{ ...cardSx, p: 2 }}>
                <Typography
                  sx={{ fontSize: 12, letterSpacing: ".1em", color: ACCENT, fontWeight: 700, mb: 0.5 }}
                >
                  INFORMATION
                </Typography>
                <ProfileTable data={model.attributes} />
              </Box>
            ) : null}
            <Box sx={{ ...cardSx, p: 2 }}>
              <Typography
                sx={{ fontSize: 12, letterSpacing: ".1em", color: ACCENT, fontWeight: 700, mb: 0.5 }}
              >
                RATES
              </Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#fff", mt: 1 }}>
                Incall
              </Typography>
              {renderRates("incall", incallRows)}
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#fff", mt: 1.5 }}>
                Outcall
              </Typography>
              {renderRates("outcall", outcallRows)}
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export { Details };
